import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, setToken } from "@/lib/api";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Plyx-shell admin login.
 *
 * Two-step flow when the ops team has set PLYX_NPS_SLACK_WEBHOOK_URL:
 *   1. POST /api/admin/2fa/init  with {email, password} → either:
 *        a) {requires_otp: true, challenge_id} — we show the OTP step
 *        b) {requires_otp: false, token, user}  — dev fallback, no Slack
 *   2. POST /api/admin/2fa/verify with {challenge_id, code} → {token, user}
 *
 * The token is persisted via setToken() so the AuthContext picks it up on
 * the next /auth/me refresh, then we navigate to /admin/plyx.
 */
const PlyxAdminLogin = () => {
  const [step, setStep] = useState("credentials");      // "credentials" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);        // seconds remaining
  const [submitting, setSubmitting] = useState(false);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin/plyx";

  // If an admin session is already live, skip the form.
  useEffect(() => {
    if (user && user.role === "admin") {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  // Countdown for the OTP expiry indicator.
  useEffect(() => {
    if (step !== "otp" || expiresIn <= 0) return;
    const t = setInterval(() => setExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, expiresIn]);

  const finishLogin = async (token, who) => {
    setToken(token);
    await refresh();
    if (who && who.role === "admin") {
      toast.success("Welcome back, admin");
      navigate(from, { replace: true });
    } else {
      toast.error("This account isn't an admin.");
    }
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/2fa/init", { email, password });
      if (data.requires_otp) {
        setChallengeId(data.challenge_id);
        setExpiresIn(data.expires_in || 300);
        setStep("otp");
        toast.success("A sign-in code has been sent to your Slack channel.");
      } else if (data.token && data.user) {
        await finishLogin(data.token, data.user);
      } else {
        toast.error("Unexpected sign-in response.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/2fa/verify", {
        challenge_id: challengeId,
        code: otp.trim(),
      });
      await finishLogin(data.token, data.user);
    } catch (err) {
      const detail = err.response?.data?.detail || "Verification failed";
      toast.error(detail);
      // On consumed/expired/over-attempt — bounce back to credentials step.
      const status = err.response?.status;
      if (status === 410 || status === 429 || status === 404) {
        setStep("credentials");
        setOtp("");
        setChallengeId(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatExpiry = () => {
    const m = Math.floor(expiresIn / 60);
    const s = expiresIn % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-16"
      data-testid="plyx-admin-login-page"
    >
      <div className="w-full max-w-md">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-4"
          style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)", color: "#0A0A0A", fontWeight: 700 }}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> Plyx · Admin
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">
          {step === "credentials" ? "Sign in to the control room" : "Enter the Slack code"}
        </h1>
        <p className="text-white/60 mb-8">
          {step === "credentials"
            ? "Use your AfriMarket admin credentials. If Slack 2FA is on, we'll send a 6-digit code to the ops channel."
            : "We posted a 6-digit code to the ops Slack channel. Enter it below to finish signing in."}
        </p>

        {step === "credentials" ? (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Admin email</label>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="plyx-admin-login-email"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="plyx-admin-login-password"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="plyx-admin-login-submit"
              className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="space-y-4" data-testid="plyx-admin-otp-form">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5 flex items-center gap-1">
                <KeyRound className="w-3.5 h-3.5" /> 6-digit code from Slack
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                data-testid="plyx-admin-otp-input"
                className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50 tracking-[0.5em] text-center text-xl"
                placeholder="••••••"
              />
              <div className="text-xs text-white/40 mt-2" data-testid="plyx-admin-otp-expiry">
                {expiresIn > 0 ? `Code expires in ${formatExpiry()}` : "Code expired — start over."}
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting || otp.length !== 6 || expiresIn <= 0}
              data-testid="plyx-admin-otp-submit"
              className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify code"}
            </button>
            <button
              type="button"
              onClick={() => { setStep("credentials"); setOtp(""); setChallengeId(null); }}
              data-testid="plyx-admin-otp-back"
              className="w-full py-2 text-xs text-white/50 hover:text-white"
            >
              ← Back to credentials
            </button>
          </form>
        )}

        <p className="mt-6 text-xs text-white/50 leading-relaxed">
          Plyx user accounts and AfriMarket admin accounts are intentionally
          separate. This page is admin-only — viewers and creators should sign
          in at <span className="underline">/plyx/login</span>.
        </p>
      </div>
    </div>
  );
};

export default PlyxAdminLogin;
